import React, { useContext } from "react";
import MetaTags from "../MetaTags.js";
import { OPEN_GRAPH_IMAGE_PATH } from "../../config/content.js";
import Content from "../../i18n/content.js";
import { container } from "../styles/common.css.js";
import { Field, Form } from "react-final-form";
import TextField from "../common/form/fields/text-field/TextField.js";
import SelectField from "../common/form/fields/select/SelectField.js";
import TextAreaField from "../common/form/fields/text-area/TextAreaField.js";
import CheckboxInput from "../common/form/fields/checkbox/CheckboxInput.js";
import { useMutation } from "@apollo/client/react/hooks/useMutation.js";
import { useQuery } from "@apollo/client/react/hooks/useQuery.js";
import ErrorMsg from "../common/errors/ErrorMsg.js";
import Spinner from "../Spinner.js";
import NotFoundErrorMsg from "../common/errors/NotFoundErrorMsg.js";
import {
    makeCheckboxValidator,
    makeEmailValidator,
    makeRequiredValidator,
} from "../common/form/formValidators.js";
import CircleSpinner from "../common/CircleSpinner.js";
import {
    formErrorMessage,
    sendButton,
} from "../common/form/fields/common.css.js";
import { formWrapper, pageTitle } from "./DataSubjectRequestForm.css.js";
import {
    CREATE_SUPPORT_MESSAGE,
    SendMessageInput,
    SendMessagePayload,
} from "../../graphql/queries/support.js";
import { VITO_APP_ID } from "../../../_common/config.js";
import {
    Country,
    GET_REQUEST_FORM_CONTENT,
    GetRequestFormContentData,
    GetRequestFormContentVariables,
} from "../../graphql/queries/data-subject-request-form.js";

import PopupMessage from "../common/form/PopupMessage.js";
import MarkdownRenderer from "../common/MarkdownRenderer.js";

interface Props {
    path: string;
}

interface FormState {
    name: string;
    email: string;
    country: string;
    purpose: string;
    comments: string;
    identityConfirm: boolean;
}
const getSortedCountries = (countries: Country[]) => {
    const countryOptions: Record<string, string> = {};
    [...countries]
        .sort((a, b) => (a.content.name > b.content.name ? 1 : -1))
        .forEach(i => {
            countryOptions[i.content.name] = i.content.name;
        });
    return countryOptions;
};
const DataSubjectRequestForm: React.FC<Props> = ({ path }) => {
    const {
        langCode,
        productSupportPage: {
            inputReqText,
            emailInvalidText,
            nameInputLbl,
            emailInputLbl,
            submitBtn,
            emailInputPlaceholder,
            nameInputPlaceholder,
            errMsgText,
        },
    } = useContext(Content);

    const { data, loading, error } = useQuery<
        GetRequestFormContentData,
        GetRequestFormContentVariables
    >(GET_REQUEST_FORM_CONTENT, {
        variables: {
            langCode,
        },
    });

    const [sendMessage, sendMessageResult] = useMutation<
        { sendMessage: SendMessagePayload },
        { input: SendMessageInput }
    >(CREATE_SUPPORT_MESSAGE);

    if (error) {
        return <ErrorMsg error={error} />;
    }

    if (loading) {
        return <Spinner />;
    }

    if (data && data.staticContent && data.countries) {
        const {
            contentLangs,
            content: {
                title,
                text,
                metaTitle,
                metaDescription,
                confirmText,
                optDeleteInfo,
                optMarketing,
                optOther,
                optProvideInfo,
                optUpdateInfo,
                countryLbl,
                purposeLbl,
                commentsLbl,
            },
        } = data.staticContent;

        const onSubmit = async ({
            name,
            email,
            country,
            purpose,
            comments,
        }: FormState) => {
            let message = `${purposeOptions[purpose]}\n\n${country}\n\n`;
            if (comments) {
                message += comments;
            }
            const dataToSave: SendMessageInput = {
                to: "Privacy",
                name,
                email,
                source: VITO_APP_ID,
                message: message,
            };
            try {
                await sendMessage({
                    variables: { input: dataToSave },
                });
            } catch {
                return { "FINAL_FORM/form-error": "ERROR" };
            }
        };

        const required = makeRequiredValidator(inputReqText);
        const countryOptions = getSortedCountries(data.countries);
        const purposeOptions: Record<string, string> = {
            provide: optProvideInfo,
            update: optUpdateInfo,
            delete: optDeleteInfo,
            marketing: optMarketing,
            other: optOther,
        };
        return (
            <>
                <MetaTags
                    path={path}
                    title={metaTitle || title}
                    description={metaDescription}
                    currentLang={langCode}
                    langs={contentLangs}
                    openGraphImage={[OPEN_GRAPH_IMAGE_PATH, "1200x630"]}
                    // openGraphTwitterImageURL={OPEN_GRAPH_TWITTER_IMAGE_PATH}
                />
                <div className={container.flex680}>
                    <h1 className={pageTitle}>{title}</h1>
                    <MarkdownRenderer source={text} />
                    <Form
                        initialValues={{
                            country: Object.entries(countryOptions)[0][0],
                            purpose: Object.entries(purposeOptions)[0][0],
                        }}
                        onSubmit={onSubmit}
                        render={({
                            handleSubmit,
                            submitSucceeded,
                            hasSubmitErrors,
                            submitting,
                            validating,
                            valid,
                            dirty,
                            form,
                        }) => {
                            return (
                                <>
                                    <form className={formWrapper}>
                                        <TextField
                                            name="name"
                                            validator={required}
                                            label={nameInputLbl}
                                            placeholder={nameInputPlaceholder}
                                        />
                                        <TextField
                                            name="email"
                                            validator={makeEmailValidator(
                                                emailInvalidText,
                                            )}
                                            label={emailInputLbl}
                                            placeholder={emailInputPlaceholder}
                                        />

                                        <SelectField
                                            name="country"
                                            label={countryLbl}
                                            options={countryOptions}
                                        />
                                        <SelectField
                                            name="purpose"
                                            label={purposeLbl}
                                            options={purposeOptions}
                                        />
                                        <TextAreaField
                                            name="comments"
                                            label={commentsLbl}
                                        />
                                        <Field
                                            name="identityConfirm"
                                            type="checkbox"
                                            component={CheckboxInput}
                                            validate={makeCheckboxValidator(
                                                inputReqText,
                                            )}
                                            text={confirmText}
                                        />
                                        <button
                                            className={sendButton}
                                            type="button"
                                            onClick={handleSubmit}
                                            disabled={
                                                submitting ||
                                                validating ||
                                                !valid
                                            }
                                        >
                                            {sendMessageResult.loading ? (
                                                <CircleSpinner />
                                            ) : (
                                                <span>{submitBtn}</span>
                                            )}
                                        </button>
                                        {hasSubmitErrors ? (
                                            <p className={formErrorMessage}>
                                                {errMsgText}
                                            </p>
                                        ) : null}
                                    </form>
                                    {submitSucceeded && dirty ? (
                                        <PopupMessage onReset={form.reset} />
                                    ) : null}
                                </>
                            );
                        }}
                    />
                </div>
            </>
        );
    }
    return <NotFoundErrorMsg />;
};

export default DataSubjectRequestForm;
